/**
 * CB Salon Finance — Finance tab for the staff dashboard
 * v1 — 2026-05-03
 *
 * Loaded by sections/cb-staff-dashboard.liquid before cb-dashboard-2026.js
 * The dashboard renders <CBFinanceTab /> when tab === "finance".
 *
 * Webhooks (CB-59 + CB-58):
 *   POST /cb-finance-data { action: 'outstanding'|'transactions'|'commission'|'approve', ... }
 *   POST /cb-bank-upload  { csv: '<text>' } OR { rows: [...] }
 *
 * Catherine-only sub-section (commission approve) is gated by the same staff PIN.
 */
(function () {
  'use strict';
  if (!window.React || !window.ReactDOM) { console.error('CBFinanceTab: React not loaded'); return; }

  var e = React.createElement;
  var useState = React.useState;
  var useEffect = React.useEffect;
  var useMemo = React.useMemo;

  var FINANCE_API = 'https://n8n.catherineboden.com/webhook/cb-finance-data';
  var BANK_API    = 'https://n8n.catherineboden.com/webhook/cb-bank-upload';

  var STYLISTS = ['Catherine', 'Amber', 'Jess L', 'Jess S'];
  var STYLIST_ACCENT = { 'Catherine': '#F5D76E', 'Amber': '#89CFF0', 'Jess L': '#F4A6C1', 'Jess S': '#C3A6D8' };

  var C = { aubergine:'#4a1942', gold:'#8B734A', cream:'#F6EFED', creamWarm:'#F7F2EB', charcoal:'#2A2131', body:'#5C5158', muted:'#9B8E8A', line:'rgba(139,115,74,0.18)', white:'#FFFFFF', success:'#3F7A5C', warn:'#C77B2B', danger:'#A33A3A' };
  var FONT_DISPLAY = "'Cormorant Garamond', Georgia, serif";

  function fmt(n) { if (n == null || isNaN(n)) return '—'; return '£' + Number(n).toFixed(2); }
  function thisMonth() { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'); }
  function periodLabel(p) {
    if (!p || !/^\d{4}-\d{2}$/.test(p)) return p || '';
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var parts = p.split('-');
    return months[parseInt(parts[1], 10) - 1] + ' ' + parts[0];
  }
  function lastNMonths(n) {
    var out = [];
    var d = new Date();
    for (var i = 0; i < n; i++) {
      var dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
      out.push(dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0'));
    }
    return out;
  }
  function postJSON(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { if (!r.ok) return r.text().then(function (t) { throw new Error('HTTP ' + r.status + ' ' + (t || '').slice(0, 200)); }); return r.json(); });
  }
  function ageColor(days) { if (days >= 7) return C.danger; if (days >= 4) return C.warn; return C.body; }

  // ---- Shared chrome ----
  var hStyle = { fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 400, color: C.charcoal, margin: 0 };
  var labelStyle = { fontFamily: 'Jost,system-ui,sans-serif', fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase', color: C.gold, fontWeight: 500 };
  var cardStyle = { background: '#fff', borderRadius: 6, padding: 18, border: '1px solid ' + C.line, boxShadow: '0 1px 3px rgba(74,25,66,.04)' };
  var btn = { padding: '8px 16px', minHeight: 36, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 500, border: '1px solid ' + C.gold, background: C.gold, color: '#fff', borderRadius: 4, cursor: 'pointer' };
  var btnGhost = Object.assign({}, btn, { background: 'transparent', color: C.gold });
  var btnDark  = Object.assign({}, btn, { background: C.aubergine, borderColor: C.aubergine });

  var thStyle = { padding: '8px 10px', textAlign: 'left', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.gold, fontWeight: 500, borderBottom: '1px solid ' + C.line };
  var tdStyle = { padding: '8px 10px', fontSize: 12, color: C.charcoal, borderBottom: '1px solid ' + C.line, verticalAlign: 'middle' };

  // =====================================================
  //  Sub-tab: Outstanding
  // =====================================================
  function OutstandingPanel() {
    var st = useState({ status: 'loading', items: [], total: 0 });
    var data = st[0], setData = st[1];

    function load() {
      setData({ status: 'loading', items: [], total: 0 });
      postJSON(FINANCE_API, { action: 'outstanding' })
        .then(function (r) { setData({ status: 'ok', items: r.items || [], total: r.total || 0, count: r.count || 0 }); })
        .catch(function (err) { setData({ status: 'error', items: [], total: 0, message: err.message }); });
    }
    useEffect(function () { load(); }, []);

    if (data.status === 'loading') return e('div', { style: { padding: 32, textAlign: 'center', color: C.muted, fontFamily: FONT_DISPLAY, fontStyle: 'italic' } }, 'Loading outstanding balances…');
    if (data.status === 'error') return e('div', { style: Object.assign({}, cardStyle, { borderLeft: '3px solid ' + C.danger }) }, 'Could not load: ' + data.message);

    return e('div', null,
      e('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 } },
        e('div', null,
          e('div', { style: labelStyle }, 'Total outstanding'),
          e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 32, color: C.charcoal, marginTop: 2 } }, fmt(data.total)),
          e('div', { style: { fontSize: 12, color: C.muted } }, data.count + ' transaction' + (data.count === 1 ? '' : 's') + ' pending')
        ),
        e('button', { onClick: load, style: btnGhost }, 'Refresh')
      ),
      data.items.length === 0
        ? e('div', { style: Object.assign({}, cardStyle, { textAlign: 'center', color: C.muted }) }, 'Nothing outstanding. ✨')
        : e('div', { style: Object.assign({}, cardStyle, { padding: 0, overflowX: 'auto' }) },
            e('table', { style: { width: '100%', borderCollapse: 'collapse', minWidth: 720 } },
              e('thead', null,
                e('tr', null,
                  e('th', { style: thStyle }, 'Date'),
                  e('th', { style: thStyle }, 'Reference'),
                  e('th', { style: thStyle }, 'Stylist'),
                  e('th', { style: thStyle }, 'Service'),
                  e('th', { style: Object.assign({}, thStyle, { textAlign: 'right' }) }, 'Balance'),
                  e('th', { style: Object.assign({}, thStyle, { textAlign: 'right' }) }, 'Age')
                )
              ),
              e('tbody', null,
                data.items.map(function (t) {
                  return e('tr', { key: t.id },
                    e('td', { style: tdStyle }, t.date || ''),
                    e('td', { style: Object.assign({}, tdStyle, { fontFamily: 'monospace', color: C.gold, fontSize: 11 }) }, t.ref || ''),
                    e('td', { style: tdStyle },
                      e('span', { style: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: STYLIST_ACCENT[t.stylist] || C.muted, marginRight: 8, verticalAlign: 'middle' } }),
                      t.stylist || ''
                    ),
                    e('td', { style: tdStyle }, t.serviceType || ''),
                    e('td', { style: Object.assign({}, tdStyle, { textAlign: 'right', fontFamily: FONT_DISPLAY, fontSize: 14 }) }, fmt(t.balanceOwed)),
                    e('td', { style: Object.assign({}, tdStyle, { textAlign: 'right', color: ageColor(t.daysOverdue || 0), fontWeight: 500 }) }, (t.daysOverdue || 0) + 'd')
                  );
                })
              )
            )
          )
    );
  }

  // =====================================================
  //  Sub-tab: Commission
  // =====================================================
  function CommissionPanel() {
    var st1 = useState(thisMonth()); var period = st1[0], setPeriod = st1[1];
    var st2 = useState({ status: 'idle', breakdown: null }); var data = st2[0], setData = st2[1];

    function calc() {
      setData({ status: 'loading', breakdown: null });
      postJSON(FINANCE_API, { action: 'commission', period: period })
        .then(function (r) { setData({ status: 'ok', breakdown: r.breakdown || {} }); })
        .catch(function (err) { setData({ status: 'error', message: err.message }); });
    }

    function approve(stylist) {
      if (!confirm('Approve ' + periodLabel(period) + ' commission for ' + stylist + '?')) return;
      postJSON(FINANCE_API, { action: 'approve', period: period, stylist: stylist, approvedBy: 'Catherine' })
        .then(function () { alert('Approved.'); })
        .catch(function (err) { alert('Failed: ' + err.message); });
    }

    var months = lastNMonths(12);

    return e('div', null,
      e('div', { style: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' } },
        e('div', null,
          e('div', { style: labelStyle }, 'Pay period'),
          e('select', { value: period, onChange: function (ev) { setPeriod(ev.target.value); }, style: { padding: '10px 14px', border: '1px solid ' + C.line, borderRadius: 4, background: '#fff', fontFamily: FONT_DISPLAY, fontSize: 16, color: C.charcoal, marginTop: 6 } },
            months.map(function (m) { return e('option', { key: m, value: m }, periodLabel(m)); })
          )
        ),
        e('button', { onClick: calc, style: btnDark }, data.status === 'loading' ? 'Calculating…' : 'Calculate / Refresh')
      ),
      data.status === 'error' && e('div', { style: { color: C.danger, marginBottom: 10 } }, data.message),
      data.status === 'ok' && data.breakdown && e('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 } },
        STYLISTS.map(function (s) {
          var b = data.breakdown[s] || { serviceRevenue: 0, extensionsRevenue: 0, productRevenue: 0, serviceCommission: 0, extensionsCommission: 0, productCommission: 0, totalCommission: 0, transactionsCount: 0, outstandingBalance: 0 };
          return e('div', { key: s, style: cardStyle },
            e('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 } },
              e('span', { style: { width: 12, height: 12, borderRadius: '50%', background: STYLIST_ACCENT[s] } }),
              e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 20, color: C.charcoal } }, s)
            ),
            e('table', { style: { width: '100%', fontSize: 12 } },
              e('tbody', null,
                row('Service', b.serviceRevenue, ' × 5%', b.serviceCommission),
                row('Extensions', b.extensionsRevenue, ' × 10%', b.extensionsCommission),
                row('Products', b.productRevenue, ' × 15%', b.productCommission)
              )
            ),
            e('div', { style: { borderTop: '1px solid ' + C.line, marginTop: 10, paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' } },
              e('div', { style: labelStyle }, 'Total'),
              e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 26, color: C.charcoal } }, fmt(b.totalCommission))
            ),
            e('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted, marginTop: 4 } },
              e('span', null, b.transactionsCount + ' transaction' + (b.transactionsCount === 1 ? '' : 's')),
              b.outstandingBalance > 0 && e('span', { style: { color: C.warn } }, fmt(b.outstandingBalance) + ' unpaid')
            ),
            e('button', { onClick: function () { approve(s); }, style: Object.assign({}, btnGhost, { width: '100%', marginTop: 12 }) }, 'Approve period')
          );
        })
      )
    );

    function row(name, rev, mul, comm) {
      return e('tr', null,
        e('td', { style: { padding: '4px 0', color: C.muted } }, name),
        e('td', { style: { padding: '4px 0', textAlign: 'right', color: C.body } }, fmt(rev)),
        e('td', { style: { padding: '4px 6px', textAlign: 'right', color: C.muted, fontSize: 10 } }, mul),
        e('td', { style: { padding: '4px 0', textAlign: 'right', color: C.charcoal, fontWeight: 500 } }, fmt(comm))
      );
    }
  }

  // =====================================================
  //  Sub-tab: Reconcile
  // =====================================================
  function ReconcilePanel() {
    var st = useState({ status: 'idle' }); var data = st[0], setData = st[1];
    var fileRef = React.useRef(null);

    function onFile(ev) {
      var f = ev.target.files && ev.target.files[0];
      if (!f) return;
      var reader = new FileReader();
      setData({ status: 'uploading', filename: f.name });
      reader.onload = function () {
        postJSON(BANK_API, { csv: reader.result })
          .then(function (r) { setData({ status: 'ok', filename: f.name, result: r }); })
          .catch(function (err) { setData({ status: 'error', filename: f.name, message: err.message }); });
      };
      reader.onerror = function () { setData({ status: 'error', filename: f.name, message: 'Could not read file' }); };
      reader.readAsText(f);
    }

    return e('div', null,
      e('div', { style: Object.assign({}, cardStyle, { textAlign: 'center', padding: 36 }) },
        e('div', { style: labelStyle }, 'Bank reconciliation'),
        e('h3', { style: Object.assign({}, hStyle, { marginTop: 6, marginBottom: 8 }) }, 'Upload a CSV statement'),
        e('p', { style: { color: C.muted, fontSize: 13, fontFamily: FONT_DISPLAY, fontStyle: 'italic', maxWidth: 480, margin: '0 auto 18px' } }, 'Export the latest statement from Tide as CSV. The system will scan the descriptions for booking references and mark matching transactions as paid.'),
        e('input', { ref: fileRef, type: 'file', accept: '.csv,text/csv', onChange: onFile, style: { display: 'none' } }),
        e('button', { onClick: function () { if (fileRef.current) fileRef.current.click(); }, style: btnDark, disabled: data.status === 'uploading' }, data.status === 'uploading' ? 'Uploading… ' + (data.filename || '') : '📄 Choose CSV file')
      ),
      data.status === 'error' && e('div', { style: Object.assign({}, cardStyle, { marginTop: 14, borderLeft: '3px solid ' + C.danger, color: C.danger }) }, 'Error: ' + data.message),
      data.status === 'ok' && data.result && renderReconResult(data.result)
    );
  }

  function renderReconResult(r) {
    var s = r.summary || {};
    var counters = [
      { label: 'Bank rows', val: s.bankRows, color: C.body },
      { label: 'Matched & paid', val: s.matched, color: C.success },
      { label: 'Unmatched (bank)', val: s.unmatchedBank, color: C.warn },
      { label: 'Amount mismatch', val: s.amountMismatch, color: C.warn },
      { label: 'Still pending', val: s.stillPending, color: C.muted },
      { label: 'Errors', val: s.errors, color: C.danger }
    ];
    return e('div', { style: { marginTop: 14 } },
      e('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 } },
        counters.map(function (c) {
          return e('div', { key: c.label, style: Object.assign({}, cardStyle, { textAlign: 'center', padding: 14 }) },
            e('div', { style: { fontFamily: FONT_DISPLAY, fontSize: 26, color: c.color } }, c.val || 0),
            e('div', { style: { fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted, marginTop: 2 } }, c.label)
          );
        })
      ),
      r.matched && r.matched.length > 0 && e('div', { style: Object.assign({}, cardStyle, { marginBottom: 12 }) },
        e('div', { style: Object.assign({}, labelStyle, { color: C.success, marginBottom: 8 }) }, 'Marked paid'),
        r.matched.map(function (m) {
          return e('div', { key: m.ref, style: { fontSize: 12, padding: '4px 0', color: C.body } }, '✓ ', e('span', { style: { fontFamily: 'monospace', color: C.gold } }, m.ref), ' — ', fmt(m.amount), ' (', m.stylist || 'unknown', ')');
        })
      ),
      r.amountMismatch && r.amountMismatch.length > 0 && e('div', { style: Object.assign({}, cardStyle, { marginBottom: 12, borderLeft: '3px solid ' + C.warn }) },
        e('div', { style: Object.assign({}, labelStyle, { color: C.warn, marginBottom: 8 }) }, 'Amount mismatches'),
        r.amountMismatch.map(function (a, i) {
          return e('div', { key: i, style: { fontSize: 12, padding: '4px 0', color: C.body } }, e('span', { style: { fontFamily: 'monospace', color: C.gold } }, a.ref), ' — bank: ', fmt(a.bankAmount), ', expected: ', fmt(a.expected), ' (diff ', fmt(a.diff), ')');
        })
      ),
      r.unmatchedBank && r.unmatchedBank.length > 0 && e('div', { style: Object.assign({}, cardStyle, { marginBottom: 12, borderLeft: '3px solid ' + C.warn }) },
        e('div', { style: Object.assign({}, labelStyle, { color: C.warn, marginBottom: 8 }) }, 'Bank rows with no matching record'),
        r.unmatchedBank.slice(0, 20).map(function (u, i) {
          return e('div', { key: i, style: { fontSize: 12, padding: '4px 0', color: C.body } }, (u.date || '') + ' — ' + (u.description || '').slice(0, 80) + ' — ' + fmt(u.amount));
        })
      )
    );
  }

  // =====================================================
  //  Sub-tab: Transactions
  // =====================================================
  function TransactionsPanel() {
    var stP = useState(''); var period = stP[0], setPeriod = stP[1];
    var stS = useState(''); var stylist = stS[0], setStylist = stS[1];
    var stStat = useState(''); var status = stStat[0], setStatus = stStat[1];
    var st = useState({ status: 'idle', items: [] }); var data = st[0], setData = st[1];

    function load() {
      setData({ status: 'loading', items: [] });
      var body = { action: 'transactions' };
      if (period) body.period = period;
      if (stylist) body.stylist = stylist;
      if (status) body.status = status;
      postJSON(FINANCE_API, body)
        .then(function (r) { setData({ status: 'ok', items: r.items || [] }); })
        .catch(function (err) { setData({ status: 'error', message: err.message }); });
    }
    useEffect(function () { load(); }, []);

    function exportCSV() {
      var rows = [['Reference','Date','Stylist','Service','Service Price','Product Total','Gross','Deposit','Balance','Payment Method','Payment Status','Date Paid']];
      data.items.forEach(function (t) {
        rows.push([t.ref, t.date, t.stylist, t.serviceType, t.servicePrice, t.productTotal, t.grossTotal, t.depositDeducted, t.balanceOwed, t.paymentMethod, t.paymentStatus, t.datePaid || '']);
      });
      var csv = rows.map(function (r) { return r.map(function (x) { var s = String(x == null ? '' : x); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; }).join(','); }).join('\n');
      var blob = new Blob([csv], { type: 'text/csv' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'salon-transactions-' + (period || 'all') + '.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    }

    var months = [''].concat(lastNMonths(12));

    return e('div', null,
      e('div', { style: { display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'flex-end' } },
        filterSel('Period', period, setPeriod, months.map(function (m) { return { value: m, label: m ? periodLabel(m) : 'All months' }; })),
        filterSel('Stylist', stylist, setStylist, [{ value: '', label: 'All' }].concat(STYLISTS.map(function (s) { return { value: s, label: s }; }))),
        filterSel('Status', status, setStatus, [{ value: '', label: 'All' }, { value: 'Pending', label: 'Pending' }, { value: 'Paid', label: 'Paid' }, { value: 'Flagged', label: 'Flagged' }, { value: 'Partial', label: 'Partial' }, { value: 'Written Off', label: 'Written Off' }]),
        e('button', { onClick: load, style: btnDark }, 'Apply'),
        e('button', { onClick: exportCSV, style: btnGhost, disabled: data.items.length === 0 }, 'Export CSV')
      ),
      data.status === 'loading' && e('div', { style: { textAlign: 'center', padding: 24, color: C.muted, fontFamily: FONT_DISPLAY, fontStyle: 'italic' } }, 'Loading…'),
      data.status === 'error' && e('div', { style: { color: C.danger } }, data.message),
      data.status === 'ok' && data.items.length === 0 && e('div', { style: Object.assign({}, cardStyle, { textAlign: 'center', color: C.muted }) }, 'No transactions match your filters.'),
      data.status === 'ok' && data.items.length > 0 && e('div', { style: Object.assign({}, cardStyle, { padding: 0, overflowX: 'auto' }) },
        e('table', { style: { width: '100%', borderCollapse: 'collapse', minWidth: 880 } },
          e('thead', null,
            e('tr', null,
              e('th', { style: thStyle }, 'Date'),
              e('th', { style: thStyle }, 'Reference'),
              e('th', { style: thStyle }, 'Stylist'),
              e('th', { style: thStyle }, 'Service'),
              e('th', { style: Object.assign({}, thStyle, { textAlign: 'right' }) }, 'Service'),
              e('th', { style: Object.assign({}, thStyle, { textAlign: 'right' }) }, 'Products'),
              e('th', { style: Object.assign({}, thStyle, { textAlign: 'right' }) }, 'Gross'),
              e('th', { style: Object.assign({}, thStyle, { textAlign: 'right' }) }, 'Balance'),
              e('th', { style: thStyle }, 'Status')
            )
          ),
          e('tbody', null,
            data.items.map(function (t) {
              var statusColor = t.paymentStatus === 'Paid' ? C.success : t.paymentStatus === 'Flagged' ? C.danger : t.paymentStatus === 'Pending' ? C.warn : C.muted;
              return e('tr', { key: t.id },
                e('td', { style: tdStyle }, t.date || ''),
                e('td', { style: Object.assign({}, tdStyle, { fontFamily: 'monospace', color: C.gold, fontSize: 11 }) }, t.ref || ''),
                e('td', { style: tdStyle },
                  e('span', { style: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: STYLIST_ACCENT[t.stylist] || C.muted, marginRight: 6, verticalAlign: 'middle' } }),
                  t.stylist || ''
                ),
                e('td', { style: tdStyle }, t.serviceType || ''),
                e('td', { style: Object.assign({}, tdStyle, { textAlign: 'right' }) }, fmt(t.servicePrice)),
                e('td', { style: Object.assign({}, tdStyle, { textAlign: 'right' }) }, fmt(t.productTotal)),
                e('td', { style: Object.assign({}, tdStyle, { textAlign: 'right', fontFamily: FONT_DISPLAY, fontSize: 14 }) }, fmt(t.grossTotal)),
                e('td', { style: Object.assign({}, tdStyle, { textAlign: 'right', color: t.balanceOwed > 0 ? C.warn : C.success }) }, fmt(t.balanceOwed)),
                e('td', { style: Object.assign({}, tdStyle, { color: statusColor, fontWeight: 500, fontSize: 11, letterSpacing: 1 }) }, (t.paymentStatus || '').toUpperCase())
              );
            })
          )
        )
      )
    );
  }

  function filterSel(label, value, setValue, options) {
    return e('div', null,
      e('div', { style: labelStyle }, label),
      e('select', { value: value, onChange: function (ev) { setValue(ev.target.value); }, style: { padding: '8px 12px', border: '1px solid ' + C.line, borderRadius: 4, background: '#fff', marginTop: 4 } },
        options.map(function (o) { return e('option', { key: o.value, value: o.value }, o.label); })
      )
    );
  }

  // =====================================================
  //  Tab shell
  // =====================================================
  function CBFinanceTab() {
    var st = useState('outstanding'); var sub = st[0], setSub = st[1];
    var subs = [
      { id: 'outstanding', label: 'Outstanding' },
      { id: 'commission',  label: 'Commission' },
      { id: 'reconcile',   label: 'Reconcile' },
      { id: 'transactions',label: 'Transactions' }
    ];
    return e('div', null,
      e('div', { style: { display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid ' + C.line, overflowX: 'auto' } },
        subs.map(function (s) {
          var active = sub === s.id;
          return e('button', { key: s.id, onClick: function () { setSub(s.id); }, style: { background: 'transparent', border: 'none', borderBottom: active ? '2px solid ' + C.gold : '2px solid transparent', padding: '10px 16px', fontSize: 11, fontWeight: active ? 600 : 400, color: active ? C.charcoal : C.gold, cursor: 'pointer', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: -1 } }, s.label);
        })
      ),
      sub === 'outstanding'  && e(OutstandingPanel,  null),
      sub === 'commission'   && e(CommissionPanel,   null),
      sub === 'reconcile'    && e(ReconcilePanel,    null),
      sub === 'transactions' && e(TransactionsPanel, null)
    );
  }

  // Expose for the staff dashboard to render
  window.CBFinanceTab = CBFinanceTab;
})();
